export interface AppResultSuccess<T> {
  ok: true;
  data: T;
}

export interface AppResultFailure {
  ok: false;
  errorCode: string;
  message: string;
  cause?: unknown;
}

export type AppResult<T> = AppResultSuccess<T> | AppResultFailure;

export function ok<T>(data: T): AppResult<T> {
  return { ok: true, data };
}

export function fail(errorCode: string, message: string, cause?: unknown): AppResult<never> {
  return { ok: false, errorCode, message, cause };
}

export function unwrapOrThrow<T>(result: AppResult<T>): T {
  if (result.ok === true) {
    return result.data;
  }
  throw new Error((result as AppResultFailure).message);
}

/** Map the success value of a Result, preserving failures. */
export function mapResult<T, U>(result: AppResult<T>, fn: (data: T) => U): AppResult<U> {
  if (result.ok) return ok(fn(result.data));
  return result as AppResultFailure;
}

/** Wrap a Promise into an AppResult — catches all exceptions. */
export async function fromPromise<T>(
  promise: Promise<T>,
  errorCode: string,
  message: string,
): Promise<AppResult<T>> {
  try {
    return ok(await promise);
  } catch (error) {
    return fail(errorCode, message, error);
  }
}

/** Extract the error message from an AppResult failure, or null if success. */
export function errorMessage(result: AppResult<unknown>): string | null {
  return result.ok ? null : (result as AppResultFailure).message;
}
